import React, { useState, useEffect, useRef } from 'react';
import { Menu, Sun, Moon, Bell, X, Check, Trash2, Download, Volume2, VolumeX } from 'lucide-react';
import { promptInstall, subscribeInstallState } from '../../utils/pwaInstall';
import { subscribeFleetSoundMuted, toggleFleetSoundMuted } from '../../utils/fleetSound';
import NotificationSettingsPopover from './NotificationSettingsPopover';
import ScopeBadge from './ScopeBadge';
import NotifyIcon from '../common/NotifyIcon';
import './Header.css';

const Header = ({ title, onToggleSidebar, userInfo, fleetName, fleetLogo, onNavigate, notifications = [], onClearNotifications, onMarkNotificationRead, accessCtx }) => {
  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem('fleet-theme') || 'light';
    } catch {
      return 'light';
    }
  });
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const notifRef = useRef(null);
  /* Kurulum istemi tarayıcıdan gelmediyse (zaten kurulu / Chrome dışı) buton hiç çıkmaz. */
  const [installAvailable, setInstallAvailable] = useState(false);

  const [soundMuted, setSoundMuted] = useState(false);

  useEffect(() => subscribeInstallState((s) => setInstallAvailable(s.available && !s.installed)), []);
  useEffect(() => subscribeFleetSoundMuted(setSoundMuted), []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try {
      localStorage.setItem('fleet-theme', theme);
    } catch {}
  }, [theme]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifPanel(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleTheme = () => setTheme(prev => prev === 'dark' ? 'light' : 'dark');

  /* Rozet, oturumu AÇAN kişiyi gösterir: sorumlu kendi adını ve rolünü görmeli;
     filo adını göstermek onu filo yöneticisi sanmasına yol açıyordu. */
  const isSupervisor = !!accessCtx && !accessCtx.isOwner;
  const displayName = isSupervisor
    ? (accessCtx.displayName || userInfo?.name || 'Bölge Sorumlusu')
    : (fleetName || userInfo?.name || 'Filo');
  const roleLabel = isSupervisor ? 'Bölge Sorumlusu' : 'Filo Yöneticisi';
  const unreadCount = notifications.filter(n => !n.read).length;

  const getInitials = () => {
    const names = displayName.split(' ');
    if (names.length >= 2) return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
    return displayName.substring(0, 2).toUpperCase();
  };

  const formatTime = (t) => {
    if (!t) return '';
    const d = new Date(t);
    const now = new Date();
    const diffMin = Math.floor((now - d) / 60000);
    if (diffMin < 1) return 'Az önce';
    if (diffMin < 60) return `${diffMin} dk önce`;
    if (diffMin < 1440) return `${Math.floor(diffMin / 60)} sa önce`;
    return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' });
  };

  const typeIcons = {
    CourierEmergency: 'siren',
    CourierInvoiceUploaded: 'file',
    FleetPaymentCreated: 'wallet',
    FleetPaymentApproved: 'check',
    FleetPaymentPaid: 'wallet',
    FleetPaymentRejected: 'alert',
    FleetInvoiceApproved: 'check',
    FleetInvoiceRejected: 'alert',
  };

  return (
    <header className="fleet-header">
      <div className="header-left">
        <button className="mobile-menu-btn" onClick={onToggleSidebar} aria-label="Toggle menu">
          <Menu size={24} />
        </button>
        <h1 className="header-title">{title || 'Anasayfa'}</h1>
        {/* Sahipte gösterilmez — kapsamı filonun tamamı, rozetin bir anlamı yok. */}
        {accessCtx && !accessCtx.isOwner && <ScopeBadge areas={accessCtx.areas} />}
      </div>

      <div className="header-right">
        <div className="header-notif-wrap" ref={notifRef}>
          <button className="header-notif-btn" onClick={() => setShowNotifPanel(p => !p)}>
            <Bell size={18} />
            {unreadCount > 0 && <span className="header-notif-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>}
          </button>

          {showNotifPanel && (
            <div className="header-notif-panel">
              <div className="notif-panel-header">
                <span className="notif-panel-title">Bildirimler</span>
                {notifications.length > 0 && (
                  <button className="notif-clear-btn" onClick={() => { onClearNotifications?.(); setShowNotifPanel(false); }}>
                    <Trash2 size={13} /> Temizle
                  </button>
                )}
              </div>
              <div className="notif-panel-list">
                {notifications.length === 0 ? (
                  <div className="notif-empty">Bildirim yok</div>
                ) : notifications.slice(0, 20).map(n => (
                  <div key={n.id} className={`notif-item ${n.read ? 'read' : 'unread'}`} onClick={() => onMarkNotificationRead?.(n.id)}>
                    <span className="notif-icon"><NotifyIcon name={typeIcons[n.type] || 'bell'} size={16} /></span>
                    <div className="notif-body">
                      <div className="notif-title">{n.title}</div>
                      <div className="notif-message">{n.message}</div>
                      <div className="notif-time">{formatTime(n.time)}</div>
                    </div>
                    {!n.read && <span className="notif-dot" />}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Bildirim sesi aç/kapa — karar localStorage'da kalıcı. Açıldığında kısa bir
            doğrulama sesi çalar (kullanıcı duyduğundan emin olsun). */}
        <button
          className="theme-toggle-btn"
          onClick={() => toggleFleetSoundMuted()}
          title={soundMuted ? 'Bildirim sesini aç' : 'Bildirim sesini kapat'}
          aria-label={soundMuted ? 'Bildirim sesini aç' : 'Bildirim sesini kapat'}
        >
          {soundMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
        </button>

        <NotificationSettingsPopover />

        {/* Kurulum daveti kapatıldıysa da kurulum buradan her zaman erişilebilir kalır. */}
        {installAvailable && (
          <button
            className="theme-toggle-btn"
            onClick={() => promptInstall()}
            title="Uygulama olarak kur"
            aria-label="Uygulama olarak kur"
          >
            <Download size={18} />
          </button>
        )}

        <button className="theme-toggle-btn" onClick={toggleTheme} aria-label={theme === 'dark' ? 'Açık temaya geç' : 'Koyu temaya geç'}>
          {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
        </button>

        <button className="header-user" onClick={() => onNavigate?.('profile')}>
          {/* Firma logosu yüklüyse rozette baş harfler yerine logo görünür. */}
          <div className={`header-user-avatar ${fleetLogo ? 'has-logo' : ''}`}>
            {fleetLogo ? <img src={fleetLogo} alt={fleetName || 'Firma logosu'} /> : getInitials()}
          </div>
          <div className="header-user-info">
            <div className="header-user-name">{displayName}</div>
            <div className="header-user-role">{roleLabel}</div>
          </div>
        </button>
      </div>
    </header>
  );
};

export default Header;
